/**
 * A leaderboard: listings ordered by what their companies have paid to be seen.
 *
 * Each row states the amount publicly, because that figure is the product's
 * central signal for a job seeker, and reveals on hover what it would cost to
 * claim the position, because transparent pricing is what brings a company
 * back. The whole row is a link — the amount is information, not a control.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  EMPLOYMENT_TYPE_LABELS,
  getCategory,
  getSubcategory,
  type RankedEntry,
} from '@bidtohire/shared';

import { priceToClaimRow } from '../lib/bid-pricing';
import { compactCompensationLabel, shortTimeAgo } from '../lib/format';
import { Money } from './Money';
import { CompanyMark, LeadCaret } from './ui';

/**
 * "Northbeam Systems · Software Engineering → Backend · Full-time"
 *
 * The employment type carries its own class because it is the part the board
 * gives up first on a narrow screen — the company, the field and the amount
 * all say more to a job seeker than "Full-time" does.
 */
function Subline({ entry }: { readonly entry: RankedEntry }): ReactNode {
  const category = getCategory(entry.category)?.name;
  const subcategory = getSubcategory(entry.category, entry.subcategory)?.name;
  const scope =
    category === undefined
      ? undefined
      : subcategory === undefined
        ? category
        : `${category} → ${subcategory}`;
  const employment = entry.employmentType
    .map((value) => EMPLOYMENT_TYPE_LABELS[value])
    .join(', ');

  return (
    <span className="board-sub">
      {entry.company.name}
      {scope === undefined ? null : ` · ${scope}`}
      {employment === '' ? null : <span className="sub-employment">{` · ${employment}`}</span>}
    </span>
  );
}

export function BoardRow({
  entry,
  boardTotalsCents,
  ranked = true,
}: {
  readonly entry: RankedEntry;
  /** Every total on this board, so a row's price knows what it clears. */
  readonly boardTotalsCents: readonly number[];
  /** False where the order is not a board position — a company's own listings. */
  readonly ranked?: boolean;
}): ReactNode {
  // The rank quoted is the one the money lands on, never the row's own. A
  // dollar over a tied row clears every listing tied with it, so on a board
  // sitting flat at the floor every row is really an offer of first place, and
  // the label has to say so rather than name the row it was read from.
  const offer = ranked ? priceToClaimRow(entry.totalPaidCents, boardTotalsCents) : undefined;

  return (
    <Link
      className={ranked && entry.rank === 1 ? 'board-row is-leader' : 'board-row'}
      to={`/jobs/${entry.jobId}`}
    >
      {ranked ? (
        <span className="board-rank">
          <span className="visually-hidden">Position </span>
          {entry.rank}
          {entry.rank === 1 ? <LeadCaret /> : null}
        </span>
      ) : (
        <span className="board-rank" aria-hidden="true" />
      )}

      <span className="board-logo-mobile">
        <CompanyMark company={entry.company} size="sm" />
      </span>

      <span className="board-role">
        <CompanyMark company={entry.company} />
        <span style={{ minWidth: 0 }}>
          <span className="board-title">{entry.title}</span>
          <Subline entry={entry} />
        </span>
      </span>

      <span className="board-cell mono">
        {compactCompensationLabel(entry.compensationLabel) ?? 'On application'}
      </span>

      <span className="board-cell">{entry.locationLabel ?? '—'}</span>

      <span className="board-paid">
        {/* Every row on a board has been paid for: a payment is what puts a
            listing here at all, so there is no unpaid case to render. */}
        <span className="paid-label">Paid</span>
        <Money cents={entry.totalPaidCents} className="amount" />
        {offer === undefined ? null : (
          <span className="claim mono">
            {offer.rank === 1 ? 'Take #1 for ' : `Claim #${offer.rank} for `}
            <Money cents={offer.priceCents} />
          </span>
        )}
      </span>

      <span className="board-cell is-numeric">
        {entry.lastBidAt === undefined ? (
          <span className="faint">—</span>
        ) : (
          <>
            <span className="mono" style={{ display: 'block', fontSize: 13 }}>
              {shortTimeAgo(entry.lastBidAt)} ago
            </span>
            <span className="board-sub" style={{ display: 'block' }}>
              last raised
            </span>
          </>
        )}
      </span>

      {/* Below the board's breakpoint this line stands in for the hidden
          columns. The paid total is not among them: it keeps its place on the
          row's first line at every width. */}
      <span className="board-facts">
        <span className="mono">
          {compactCompensationLabel(entry.compensationLabel) ?? 'On application'}
        </span>
        {entry.locationLabel === undefined ? null : <span>{entry.locationLabel}</span>}
        {entry.lastBidAt === undefined ? null : (
          <span className="mono">{shortTimeAgo(entry.lastBidAt)} ago</span>
        )}
      </span>
    </Link>
  );
}

export function Leaderboard({
  entries,
  showHeader = true,
  ranked = true,
}: {
  readonly entries: readonly RankedEntry[];
  readonly showHeader?: boolean;
  /**
   * False when the rows are a list rather than a board — a company's own
   * listings, say, whose order is not a position anyone can buy. An index
   * rendered as a rank would be priced as one, and that price would name a
   * ladder that exists on no board.
   */
  readonly ranked?: boolean;
}): ReactNode {
  const boardTotalsCents = entries.map((entry) => entry.totalPaidCents);

  return (
    <>
      {showHeader ? (
        <div className="board-head" aria-hidden="true">
          <div>{ranked ? 'Rank' : 'Listing'}</div>
          <div>Role</div>
          <div>Salary range</div>
          <div>Location</div>
          <div style={{ textAlign: 'right' }}>Paid</div>
          <div style={{ textAlign: 'right' }}>Activity</div>
        </div>
      ) : null}
      {entries.map((entry) => (
        <BoardRow
          key={`${entry.jobId}-${entry.rank}`}
          entry={entry}
          boardTotalsCents={boardTotalsCents}
          ranked={ranked}
        />
      ))}
    </>
  );
}
